// permissions-catalog.js
// El árbol de permisos se deriva del menú de ACL (sidebar/{APP_CODE}).
// Niveles: Módulo → Recurso → (CRUD + botones de acción del recurso).
//   modules = [{ code, name, icon, resources: [{ code, name, buttons: [{ code, name }] }] }]
//   - code de módulo  = uri del ítem padre
//   - code de recurso = uri del sub-ítem (o del propio módulo si no tiene hijos)
//   - buttons         = acciones/botones que ACL expone para ese recurso (resource-uri)
// 'read' (R) es la acción maestra del recurso: sin R no hay acceso (CRUD y botones la requieren).

export const ACTIONS = {
	create: { code: 'create', short: 'C', label: 'Crear' },
	read: { code: 'read', short: 'R', label: 'Ver', master: true },
	update: { code: 'update', short: 'U', label: 'Editar' },
	delete: { code: 'delete', short: 'D', label: 'Eliminar' },
};

export const ACTION_ORDER = ['create', 'read', 'update', 'delete'];
const CRUD = ['create', 'read', 'update', 'delete'];

export function isCrud(actionCode) {
	return CRUD.indexOf(actionCode) > -1;
}

// Mapea la lista de botones/acciones que entrega ACL a { code, name }.
export function mapButtons(list = []) {
	return (list || [])
		.map(a => ({ code: a.codeWeb || a.code || a.uri, name: a.name || a.label || a.codeWeb }))
		.filter(b => !!b.code);
}

// El ACL puede devolver `name` como texto plano o como JSON multilenguaje
// ({ es: { name }, en: { name } }). Devuelve el nombre en el idioma pedido.
export function resolveName(name, lang = 'es') {
	if (name && typeof name === 'object') {
		return (name[lang] && name[lang].name) || (name.en && name.en.name) || '';
	}
	if (typeof name === 'string') {
		const t = name.trim();
		if (t.charAt(0) === '{' && t.indexOf('"name"') > -1) {
			try {
				const o = JSON.parse(t);
				return (o[lang] && o[lang].name) || (o.en && o.en.name) || name;
			} catch (e) { return name; }
		}
	}
	return name;
}

// Construye un mapa { uri: nombre } a partir del menú REAL (sidebar/{APP_CODE}),
// que es el que el usuario ve en la barra lateral. Incluye módulos y recursos.
// Sirve para que la pantalla de permisos muestre EXACTAMENTE los nombres del menú.
export function buildNameMap(menuItems = []) {
	const map = {};
	(menuItems || []).forEach((item) => {
		if (item.uri) map[item.uri] = resolveName(item.name);
		const children = (item.resource && item.resource.data) || [];
		children.forEach((sub) => {
			if (sub.uri) map[sub.uri] = resolveName(sub.name);
		});
	});
	return map;
}

// Reemplaza los nombres del árbol (módulos y recursos) por los del menú real,
// cuando el uri exista en el mapa. Si no existe, conserva el nombre original.
export function applyNameMap(modules = [], nameMap = {}) {
	return (modules || []).map((m) => {
		const resources = (m.resources || []).map(r => ({
			...r,
			name: nameMap[r.code] || r.name,
		}));
		return { ...m, name: nameMap[m.code] || m.name, resources };
	});
}

// Ordena el árbol (módulos y recursos) igual que el menú lateral: por `position`.
// Usa el menú REAL (sidebar/{APP_CODE}) como referencia de orden. Lo que no esté
// en el menú va al final, conservando su orden relativo.
export function applyMenuOrder(modules = [], menuItems = []) {
	const sortedMenu = (menuItems || []).slice()
		.sort((a, b) => (a.position || 0) - (b.position || 0));
	const modIndex = {};
	const resIndex = {};
	sortedMenu.forEach((item, mi) => {
		const muri = item.uri || String(item.id);
		modIndex[muri] = mi;
		const rmap = {};
		((item.resource && item.resource.data) || []).slice()
			.sort((a, b) => (a.position || 0) - (b.position || 0))
			.forEach((sub, ri) => { if (sub.uri) rmap[sub.uri] = ri; });
		resIndex[muri] = rmap;
	});
	const rank = (map, key) =>
		(Object.prototype.hasOwnProperty.call(map, key) ? map[key] : 9999);
	const out = (modules || []).map(m => ({ ...m, resources: (m.resources || []).slice() }));
	out.forEach((m) => {
		const rmap = resIndex[m.code] || {};
		m.resources.sort((a, b) => rank(rmap, a.code) - rank(rmap, b.code));
	});
	out.sort((a, b) => rank(modIndex, a.code) - rank(modIndex, b.code));
	return out;
}

// Respuesta de ACL (data del sidebar) → árbol de módulos/recursos (+ botones embebidos si vienen).
export function normalizeSidebar(items = []) {
	return (items || []).map((item) => {
		const children = (item.resource && item.resource.data) || [];
		const toRes = s => ({
			code: s.uri,
			name: resolveName(s.name),
			buttons: mapButtons(s.actions),
		});
		const resources = children.length ? children.map(toRes) : [toRes(item)];
		return {
			code: item.uri || String(item.id),
			name: resolveName(item.name),
			icon: item.pathIcon || '',
			resources,
		};
	});
}

// Backend [{module, resource, actions:[...]}] → mapa editable { resourceCode: Set(actions) }
export function toPermMap(serverPermissions = []) {
	const map = {};
	(serverPermissions || []).forEach((p) => {
		if (!map[p.resource]) map[p.resource] = new Set();
		(p.actions || []).forEach(a => map[p.resource].add(a));
	});
	return map;
}

// Árbol + mapa del front → payload backend (CRUD y botones mezclados por recurso).
export function toPayload(modules, permMap) {
	const out = [];
	(modules || []).forEach((m) => {
		m.resources.forEach((res) => {
			const set = permMap[res.code] || new Set();
			const crud = ACTION_ORDER.filter(a => set.has(a));
			const buttons = (res.buttons || []).map(b => b.code).filter(c => set.has(c));
			const actions = crud.concat(buttons);
			if (actions.length) out.push({ module: m.code, resource: res.code, actions });
		});
	});
	return out;
}

// ---- Formato ACL: mapa por uri { "<uri>": [acciones] } ----

// Árbol + mapa efectivo → { uri: [acciones] } (lo que guarda el ACL).
export function toResourcesMap(modules, permMap) {
	const resources = {};
	(modules || []).forEach((m) => {
		m.resources.forEach((res) => {
			const set = permMap[res.code] || new Set();
			const crud = ACTION_ORDER.filter(a => set.has(a));
			const buttons = (res.buttons || []).map(b => b.code).filter(c => set.has(c));
			const actions = crud.concat(buttons);
			if (actions.length) resources[res.code] = actions;
		});
	});
	return resources;
}

// { uri: [acciones] } (del ACL) → { resourceCode: Set(acciones) }
export function fromResourcesMap(resources) {
	const map = {};
	Object.keys(resources || {}).forEach((uri) => {
		map[uri] = new Set(resources[uri] || []);
	});
	return map;
}

// ---- Permisos por USUARIO (overrides sobre el estándar del rol) ----

// Aplica los overrides del usuario sobre el mapa base del rol → mapa efectivo.
// baseMap: { resourceCode: Set(actions) }   overrides: [{ resource, action, effect }]
export function applyOverrides(baseMap = {}, overrides = []) {
	const map = {};
	Object.keys(baseMap).forEach((rc) => { map[rc] = new Set(baseMap[rc]); });
	(overrides || []).forEach((o) => {
		if (!map[o.resource]) map[o.resource] = new Set();
		if (o.effect === 'deny') map[o.resource].delete(o.action);
		else map[o.resource].add(o.action);
	});
	return map;
}

// Diferencia entre el efectivo (lo que marcó el admin) y la base del rol →
// lista de overrides [{ module, resource, action, effect, type }].
// effect: 'allow' = se agregó respecto al rol · 'deny' = se quitó respecto al rol.
export function toOverrides(modules, baseMap = {}, permMap = {}) {
	const out = [];
	const crud = new Set(ACTION_ORDER);
	(modules || []).forEach((m) => {
		m.resources.forEach((res) => {
			const base = baseMap[res.code] || new Set();
			const eff = permMap[res.code] || new Set();
			const codes = new Set([
				...ACTION_ORDER,
				...(res.buttons || []).map(b => b.code),
				...base,
				...eff,
			]);
			codes.forEach((a) => {
				const inBase = base.has(a);
				const inEff = eff.has(a);
				if (inBase === inEff) return;
				out.push({
					module: m.code,
					resource: res.code,
					action: a,
					effect: inEff ? 'allow' : 'deny',
					type: crud.has(a) ? 'crud' : 'button',
				});
			});
		});
	});
	return out;
}



// WEBPACK FOOTER //
// ./src/shared/permissions-catalog.js